import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

export default function OnboardingPage() {
  const { t } = useTranslation();

  const badges = [
    { icon: 'shield', title: t('onboardingPage.badges.secure.title'), sub: t('onboardingPage.badges.secure.sub') },
    { icon: 'bolt', title: t('onboardingPage.badges.agile.title'), sub: t('onboardingPage.badges.agile.sub') },
    { icon: 'smart_toy', title: t('onboardingPage.badges.ai.title'), sub: t('onboardingPage.badges.ai.sub') },
  ];

  useEffect(() => {
    document.title = t('onboardingPage.pageTitle');
  }, [t]);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [refNumber, setRefNumber] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/api/onboarding', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), email: email.trim().toLowerCase() }),
      });
      const raw = await res.text();
      let data = {};
      try {
        data = raw ? JSON.parse(raw) : {};
      } catch {
        throw new Error(t('onboardingPage.errors.connectionError'));
      }
      if (!res.ok || !data.success) {
        throw new Error(data.message || data.error || t('onboardingPage.errors.requestFailed'));
      }
      setSuccess(true);
      setRefNumber(data.ref || '');
    } catch (err) {
      setError(err.message || t('onboardingPage.errors.connectionError'));
    }
    setLoading(false);
  }

  return (
    <main id="main-content" className="min-h-screen bg-[#0F0F0F] pt-40 pb-20 flex flex-col items-center px-6">
      {/* Glow background */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-purple-primary/10 rounded-full blur-[150px] pointer-events-none" />

      {/* Hero */}
      <motion.div
        className="text-center max-w-2xl relative z-10 mb-12"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-black font-headline tracking-tight text-white mb-4">
          {t('onboardingPage.heroTitle')}{' '}
          <span className="bg-gradient-to-r from-purple-primary to-accent-magenta bg-clip-text text-transparent">{t('onboardingPage.heroTitleHighlight')}</span>
        </h1>
        <p className="text-on-surface-variant text-lg leading-relaxed">
          {t('onboardingPage.heroSubtitle')}
        </p>
      </motion.div>

      {/* Badges */}
      <motion.div
        className="flex flex-wrap gap-4 justify-center mb-12 relative z-10"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.6 }}
      >
        {badges.map((b) => (
          <div key={b.title} className="flex flex-col items-center gap-1.5 px-6 py-4 bg-[#141414] border border-[#3B323A] rounded-xl min-w-[160px]">
            <span className="material-symbols-outlined text-2xl text-purple-primary">{b.icon}</span>
            <span className="font-headline font-semibold text-sm text-white text-center">{b.title}</span>
            <span className="text-xs text-gray-500 text-center">{b.sub}</span>
          </div>
        ))}
      </motion.div>

      {/* Form or Success */}
      <motion.div
        className="w-full max-w-md relative z-10"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.6 }}
      >
        {success ? (
          <div className="bg-[#141414] border border-[#2ECC71]/30 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#2ECC71]/10 flex items-center justify-center">
              <span className="material-symbols-outlined text-3xl text-[#2ECC71]">check_circle</span>
            </div>
            <h2 className="text-2xl font-black font-headline text-white mb-3">{t('onboardingPage.success.title')}</h2>
            {refNumber && (
              <p className="text-purple-primary text-sm font-mono mb-4">{t('onboardingPage.success.refPrefix')} <strong>{refNumber}</strong></p>
            )}
            <p className="text-on-surface-variant text-sm leading-relaxed mb-3">
              {t('onboardingPage.success.message')}
            </p>
            <p className="text-gray-600 text-xs">
              {t('onboardingPage.success.responseTime')}
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-[#141414] border border-[#3B323A] rounded-2xl p-8">
            <div className="mb-5">
              <label htmlFor="onb-name" className="block text-xs font-medium text-on-surface-variant/70 mb-1.5 uppercase tracking-wider">
                {t('onboardingPage.form.fullName')}
              </label>
              <input
                id="onb-name"
                type="text"
                required
                minLength={2}
                maxLength={100}
                placeholder={t('onboardingPage.form.fullNamePlaceholder')}
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={loading}
                className="w-full px-4 py-3 bg-[#0F0F0F] border border-[#3B323A] rounded-lg text-white text-sm placeholder-gray-600 focus:border-purple-primary focus:ring-1 focus:ring-purple-primary/50 transition-all outline-none"
              />
            </div>
            <div className="mb-5">
              <label htmlFor="onb-email" className="block text-xs font-medium text-on-surface-variant/70 mb-1.5 uppercase tracking-wider">
                {t('onboardingPage.form.email')}
              </label>
              <input
                id="onb-email"
                type="email"
                required
                placeholder={t('onboardingPage.form.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                className="w-full px-4 py-3 bg-[#0F0F0F] border border-[#3B323A] rounded-lg text-white text-sm placeholder-gray-600 focus:border-purple-primary focus:ring-1 focus:ring-purple-primary/50 transition-all outline-none"
              />
            </div>

            {error && (
              <div className="mb-4 px-4 py-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                {error}
              </div>
            )}

            <motion.button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-gradient-to-r from-purple-primary to-accent-magenta text-white rounded-xl font-bold font-headline shadow-lg shadow-purple-primary/20 relative overflow-hidden group disabled:opacity-60 disabled:cursor-not-allowed"
              whileHover={!loading ? { scale: 1.02, boxShadow: '0 0 30px rgba(168,67,151,0.3)' } : {}}
              whileTap={!loading ? { scale: 0.98 } : {}}
            >
              <span className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <span className="relative flex items-center justify-center gap-2">
                {loading ? t('onboardingPage.form.submitting') : t('onboardingPage.form.submit')}
                {!loading && <span className="material-symbols-outlined text-[18px]">arrow_forward</span>}
              </span>
            </motion.button>

            <p className="text-center text-gray-600 text-xs mt-4 flex items-center justify-center gap-1.5">
              <span className="material-symbols-outlined text-purple-primary text-sm">lock</span>
              {t('onboardingPage.form.securityNote')}
            </p>
          </form>
        )}
      </motion.div>
    </main>
  );
}
